import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

// Define interfaces for TagValue and API responses
export interface TagValue {
  id: string;
  tagDefinitionId: string;
  value: string;
  displayName: string | null;
  description: string | null;
  metadata: Record<string, unknown> | null;
  color: string | null;
  sortOrder: number;
  isDefault: boolean;
  isActive: boolean;
  createdByUserId: string;
  createdByOrganizationId: string | null;
  createdByDivisionId: string | null;
  visibleToDivisions: string[] | null;
  createdAt: string;
  updatedAt: string;
}

interface TagValueListResponse {
  tagValues: TagValue[];
}

export interface CreateTagValueRequest {
  value: string;
  displayName?: string | null;
  description?: string | null;
  metadata?: Record<string, unknown> | null;
  color?: string | null;
  sortOrder?: number;
  isDefault?: boolean;
  isActive?: boolean;
  visibleToDivisions?: string[] | null;
}

export interface UpdateTagValueRequest {
  displayName?: string | null;
  description?: string | null;
  metadata?: Record<string, unknown> | null;
  color?: string | null;
  sortOrder?: number;
  isDefault?: boolean;
  isActive?: boolean;
  visibleToDivisions?: string[] | null;
}

interface TagValueResponse {
  id: string;
  tagDefinitionId: string;
  value: string;
  displayName: string | null;
  description: string | null;
  metadata: Record<string, unknown> | null;
  color: string | null;
  sortOrder: number;
  isDefault: boolean;
  isActive: boolean;
  createdByUserId: string;
  createdByOrganizationId: string | null;
  createdByDivisionId: string | null;
  visibleToDivisions: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiErrorResponse {
  error: string;
  description?: string;
}

const backendApi = config.backendApi;

// Fetch tag values for a specific tag definition.
// Exported so callers that need values for *several* definitions at once can drive
// their own `useQueries` off the same fetcher and query key, instead of a hook per
// definition — see `useTagValueLabels`.
export const fetchTagValuesByDefinition = async (tagDefinitionId: string): Promise<TagValue[]> => {
  const response = await axios.get<TagValueListResponse>(
    `${backendApi}/api/tag-values/by-definition/${tagDefinitionId}`,
    { withCredentials: true }
  );
  return response.data.tagValues;
};

// Create a new tag value
const createTagValue = async (tagDefinitionId: string, data: CreateTagValueRequest): Promise<TagValueResponse> => {
  const response = await axios.post<TagValueResponse>(
    `${backendApi}/api/tag-values/for-definition/${tagDefinitionId}`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

// Update an existing tag value
const updateTagValue = async (id: string, data: UpdateTagValueRequest): Promise<TagValueResponse> => {
  const response = await axios.patch<TagValueResponse>(
    `${backendApi}/api/tag-values/${id}`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

// Delete a tag value
const deleteTagValue = async (id: string): Promise<void> => {
  await axios.delete(`${backendApi}/api/tag-values/${id}`, { withCredentials: true });
};

// Hook to fetch tag values for a specific tag definition
export function useTagValuesByDefinition(
  organizationId: string | null | undefined,
  tagDefinitionId: string | null | undefined
) {
  const contextReady = useActiveContextReady();

  return useQuery<TagValue[], AxiosError<ApiErrorResponse>>({
    queryKey: ['tagValues', organizationId, 'definition', tagDefinitionId],
    queryFn: () => {
      if (!organizationId || !tagDefinitionId) {
        throw new Error("organizationId and tagDefinitionId are required to fetch tag values.");
      }
      return fetchTagValuesByDefinition(tagDefinitionId);
    },
    enabled: !!organizationId && !!tagDefinitionId && contextReady,
  });
}

// Hook to create a tag value
export function useCreateTagValue() {
  const queryClient = useQueryClient();

  return useMutation<
    TagValueResponse,
    AxiosError<ApiErrorResponse>,
    { tagDefinitionId: string; data: CreateTagValueRequest }
  >({
    mutationFn: ({ tagDefinitionId, data }) => createTagValue(tagDefinitionId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tagValues'] });
    },
  });
}

// Hook to update a tag value
export function useUpdateTagValue() {
  const queryClient = useQueryClient();

  return useMutation<
    TagValueResponse,
    AxiosError<ApiErrorResponse>,
    { id: string; data: UpdateTagValueRequest }
  >({
    mutationFn: ({ id, data }) => updateTagValue(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tagValues'] });
    },
  });
}

// Hook to delete a tag value
export function useDeleteTagValue() {
  const queryClient = useQueryClient();

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  return useMutation<void, AxiosError<ApiErrorResponse>, string>({
    mutationFn: deleteTagValue,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tagValues'] });
    },
  });
}
